/* useSwipeActions.js — calm operational swipe gesture hook.
 *
 * Pointer-event-based horizontal swipe handler for operational
 * queue rows. Mobile-only by design (coarse pointer / touch),
 * never visible on desktop.
 *
 * Doctrine:
 *   - Native, invisible, fast.
 *   - Soft resistance past threshold.
 *   - Accidental-swipe prevention: abort on vertical-dominant
 *     motion or tiny deltas.
 *   - Springs back via CSS transition.
 *
 * Returns an object you spread into the row element:
 *   {
 *     ref,                  // ref to set on the swipeable element
 *     transform,            // string for inline `style.transform`
 *     transition,           // string for inline `style.transition`
 *     direction,            // null | 'right' | 'left' (committed)
 *     progressX,            // -1..1 — fraction of threshold travelled
 *     handlers,             // onTouchStart / Move / End / Cancel
 *     reset,                // call to force snap-back (e.g. after commit)
 *   }
 */
import { useCallback, useEffect, useRef, useState } from "react";

const SPRING_TRANSITION = "transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)";
const COMMIT_RATIO      = 0.32;   // travel ≥ 32% of row width = commit
const RUBBER_RATIO      = 0.55;   // resistance after commit point
const VERTICAL_LOCK     = 12;     // |Δy| in px → treat as scroll, abort
const MIN_ACTIVATE      = 8;      // |Δx| px before engaging the gesture

export function useSwipeActions({
  onSwipeRight,
  onSwipeLeft,
  enabled = true,
} = {}) {
  const ref = useRef(null);
  const stateRef = useRef({
    startX: 0,
    startY: 0,
    width: 0,
    deltaX: 0,
    engaged: false,    // pointer down + horizontal lock acquired
    aborted: false,    // vertical scroll detected → ignore until next touch
  });
  const [deltaX,    setDeltaX]    = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [direction, setDirection] = useState(null); // committed direction

  const reset = useCallback(() => {
    stateRef.current.deltaX  = 0;
    stateRef.current.engaged = false;
    stateRef.current.aborted = false;
    setDeltaX(0);
    setIsSliding(false);
  }, []);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    const t = e.touches ? e.touches[0] : e;
    stateRef.current.startX  = t.clientX;
    stateRef.current.startY  = t.clientY;
    stateRef.current.width   = ref.current?.offsetWidth || 0;
    stateRef.current.engaged = false;
    stateRef.current.aborted = false;
    setDirection(null);
    setIsSliding(false);
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    if (!enabled || stateRef.current.aborted) return;
    const t = e.touches ? e.touches[0] : e;
    const dx = t.clientX - stateRef.current.startX;
    const dy = t.clientY - stateRef.current.startY;

    // Vertical scroll dominance → abort gesture for this touch.
    if (!stateRef.current.engaged && Math.abs(dy) > VERTICAL_LOCK && Math.abs(dy) > Math.abs(dx)) {
      stateRef.current.aborted = true;
      return;
    }

    // Wait for the user to commit horizontally before engaging.
    if (!stateRef.current.engaged) {
      if (Math.abs(dx) < MIN_ACTIVATE) return;
      stateRef.current.engaged = true;
      setIsSliding(true);
    }

    // Prevent the parent from horizontally scrolling once engaged.
    if (e.cancelable) e.preventDefault();

    // Rubber-band past the commit threshold so the user feels the
    // "snap" but can still over-pull without instant runaway.
    const w = stateRef.current.width || 1;
    const sign = Math.sign(dx);
    const commitPx = w * COMMIT_RATIO;
    let visible = dx;
    if (Math.abs(dx) > commitPx) {
      const over = Math.abs(dx) - commitPx;
      visible = sign * (commitPx + over * RUBBER_RATIO);
    }
    // Hard cap at ~46% width so the row never fully escapes.
    const cap = w * 0.46;
    if (visible > cap) visible = cap;
    if (visible < -cap) visible = -cap;
    stateRef.current.deltaX = visible;
    setDeltaX(visible);
  }, [enabled]);

  const onTouchEnd = useCallback(() => {
    if (!enabled) return;
    if (!stateRef.current.engaged || stateRef.current.aborted) {
      reset();
      return;
    }
    const w = stateRef.current.width || 1;
    const commitPx = w * COMMIT_RATIO;
    const dx = stateRef.current.deltaX;
    if (dx >= commitPx) {
      setDirection("right");
      onSwipeRight?.();
    } else if (dx <= -commitPx) {
      setDirection("left");
      onSwipeLeft?.();
    }
    reset();
  }, [enabled, onSwipeRight, onSwipeLeft, reset]);

  const onTouchCancel = onTouchEnd;

  // Clear `direction` shortly after commit so callers can re-arm
  // (e.g. fade the action chip on the row).
  useEffect(() => {
    if (!direction) return;
    const id = setTimeout(() => setDirection(null), 480);
    return () => clearTimeout(id);
  }, [direction]);

  const transform  = `translate3d(${deltaX}px,0,0)`;
  const transition = isSliding ? "transform 0ms" : SPRING_TRANSITION;
  const w          = stateRef.current.width || 1;
  const progressX  = Math.max(-1, Math.min(1, deltaX / (w * COMMIT_RATIO)));

  return {
    ref,
    transform,
    transition,
    direction,
    progressX,
    deltaX,
    isSliding,
    reset,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
    },
  };
}

/**
 * Tiny detector for coarse-pointer (touch) primary devices.
 * Used to gate the swipe UI to mobile/tablet only — desktop
 * stays visually unchanged per the brief.
 */
export function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setCoarse(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return coarse;
}
